import type { PlayerOptions, PlaylistItem, Player } from '@beyondwords/player';
import type { PublicationContext } from '~/types';

/* Names of the CSS custom properties the player takes its colours from. */
export interface AudioPlayerColorVars {
  bg: string;
  fg: string;
}

export interface UseBeyondWordsPlayerOptions {
  container: Ref<HTMLElement | null>;
  colorVars: AudioPlayerColorVars;
  /*
   * Element the colour variables are read from. Defaults to <html>; the
   * frontpage player passes its own wrapper because the variables are scoped
   * to the component rather than the document.
   */
  colorScope?: Ref<HTMLElement | null>;
  /* Desktop widget width; the widget falls back to 'auto' on mobile. */
  desktopWidgetWidth?: string;
  /* Keep the widget hidden until playback starts, then reveal it. */
  revealWidgetOnPlay?: boolean;
  onPlay?: () => void;
  onTrackChange?: (payload: { sourceId?: string; auto: boolean }) => void;
}

export type AudioPlayerSource = { playlist: PlaylistItem[] } | { contentId: string | number };

const MOBILE_BREAKPOINT = 768;

/*
 * Owns the BeyondWords SDK instance: creation, teardown, listener handles,
 * theme colour sync and responsive layout. Callers supply the container and
 * the source (playlist or single contentId) and never touch the SDK directly.
 */
export default function useBeyondWordsPlayer (options: UseBeyondWordsPlayerOptions) {
  const publicConfig = useRuntimeConfig().public as unknown as PublicationContext;
  const beyondWords = publicConfig.beyondWords || {};
  const player = shallowRef<Player | null>(null);
  const playerReady = ref(false);
  const eventHandles = ref<Array<{ event: string; handle: string }>>([]);
  const stopThemeObserver = ref<(() => void) | null>(null);
  const widgetRevealed = ref(false);

  const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

  const readColors = () => {
    const scope = options.colorScope?.value || document.documentElement;
    const styles = getComputedStyle(scope);
    const backgroundColor = styles.getPropertyValue(options.colorVars.bg).trim();
    const textColor = styles.getPropertyValue(options.colorVars.fg).trim();

    return { backgroundColor, textColor, iconColor: textColor };
  };

  const applyColors = () => {
    const colors = readColors();
    if (!player.value || !colors.backgroundColor || !colors.textColor) {
      return;
    }
    player.value.backgroundColor = colors.backgroundColor;
    player.value.textColor = colors.textColor;
    player.value.iconColor = colors.iconColor;
  };

  const applyResponsiveLayout = () => {
    if (!player.value) {
      return;
    }
    const mobile = isMobile();
    player.value.widgetPosition = mobile ? 'center' : 'left';
    if (options.desktopWidgetWidth) {
      player.value.widgetWidth = mobile ? 'auto' : options.desktopWidgetWidth;
    }
    if (options.revealWidgetOnPlay) {
      player.value.widgetStyle = widgetRevealed.value ? 'standard' : 'none';
    }
  };

  const removeEventHandles = () => {
    if (player.value) {
      for (const { event, handle } of eventHandles.value) {
        player.value.removeEventListener(event, handle);
      }
    }
    eventHandles.value = [];
  };

  const destroy = () => {
    removeEventHandles();
    if (stopThemeObserver.value) {
      stopThemeObserver.value();
      stopThemeObserver.value = null;
    }
    player.value?.destroy();
    player.value = null;
    playerReady.value = false;
    widgetRevealed.value = false;
    if (options.container.value) {
      options.container.value.innerHTML = '';
    }
  };

  const registerListeners = (instance: Player) => {
    const listen = (event: string, callback: (payload: any) => void) => {
      eventHandles.value.push({ event, handle: instance.addEventListener(event, callback) });
    };

    listen('ContentAvailable', () => {
      playerReady.value = true;
    });

    listen('NoContentAvailable', () => {
      playerReady.value = false;
    });

    listen('PlaybackPlaying', () => {
      if (!widgetRevealed.value) {
        widgetRevealed.value = true;
        applyResponsiveLayout();
        options.onPlay?.();
      }
    });

    if (!options.onTrackChange) {
      return;
    }

    listen('<any>', (event: any) => {
      const change = event?.changedProps?.contentIndex;
      if (!change) {
        return;
      }
      const item = instance.content?.[change.currentValue];
      options.onTrackChange?.({ sourceId: item?.sourceId, auto: event.type === 'PlaybackEnded' });
    });
  };

  const create = async (source: AudioPlayerSource, overrides: Partial<PlayerOptions> = {}) => {
    destroy();

    if (!beyondWords.projectId || !options.container.value) {
      return;
    }

    const { default: BeyondWordsPlayer } = await import('@beyondwords/player');

    const colors = readColors();
    await nextTick();

    /* The component may have been torn down while awaiting the tick. */
    if (!options.container.value) {
      return;
    }

    const instance = new BeyondWordsPlayer.Player({
      target: options.container.value,
      projectId: Number(beyondWords.projectId),
      ...source,
      playlistStyle: 'hide',
      skipButtonStyle: beyondWords.skipButtonStyle ?? 'seconds-30',
      logoIconEnabled: false,
      widgetPosition: isMobile() ? 'center' : 'left',
      widgetStyle: options.revealWidgetOnPlay ? 'none' : 'standard',
      ...(beyondWords.playbackRate != null ? { playbackRate: beyondWords.playbackRate } : {}),
      ...(beyondWords.playbackRates != null ? { playbackRates: beyondWords.playbackRates } : {}),
      ...(colors.backgroundColor && colors.textColor ? colors : {}),
      ...overrides
    });

    player.value = instance;
    registerListeners(instance);

    const observerControls = useMutationObserver(
      [document.documentElement, document.body],
      () => applyColors(),
      { attributes: true, attributeFilter: ['class', 'data-theme'] }
    );
    stopThemeObserver.value = observerControls.stop;

    applyResponsiveLayout();
  };

  useEventListener('resize', applyResponsiveLayout);
  onUnmounted(destroy);

  return {
    isConfigured: computed(() => !!beyondWords.projectId),
    player: shallowReadonly(player),
    playerReady: readonly(playerReady),
    create,
    destroy
  };
}
